/*
 * FTP native cart drawer extras
 * -------------------------------
 * Rebuilds the pieces Kaching Cart provided on top of the theme's native <cart-drawer>:
 *   - tier message above the existing 10% / 20% stepper (stepper itself lives in bundle-upsell-modal.js)
 *   - per-line variant (color) dropdown
 *   - "Often bought together" carousel (Shopify complementary recommendations)
 *   - full price / discount / subtotal breakdown above the subtotal
 *   - trust badges row under checkout
 *   - empty-state Continue shopping -> /collections
 * Everything is injected idempotently via a MutationObserver, because cart-drawer.js replaces the
 * drawer's HTML on every cart update. Extras are rebuilt from cached data in the same tick as the
 * re-render so the drawer does not visibly flash. Discounts themselves are native Shopify automatic discounts.
 *
 * NOTE (quantity-increment popup fix): the upsell box and the per-line variant dropdown live inside
 * #CartDrawer-CartItems, so their <select> 'change' events bubble up to the theme's cart-items change
 * handler, which treats every change as a quantity edit and shows "You can only add this item in
 * increments of undefined". We stop 'change' from bubbling out of those two containers.
 */
(function () {
  if (window.__ftpCartExtras) return;
  window.__ftpCartExtras = true;

  const productCache = {};
  let lastCart = null;
  let recs = { anchor: null, all: null, idx: 0 };
  const $ = (s, r) => (r || document).querySelector(s);
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const esc = (v) => {
    const d = document.createElement('div');
    d.textContent = v == null ? '' : String(v);
    return d.innerHTML;
  };
  const money = (c) => '$' + (c / 100).toFixed(2);
  const parseMoney = (t) => Math.round(parseFloat(String(t || '').replace(/[^0-9.]/g, '') || '0') * 100);
  const scale = (src, size) => (src ? src.replace(/(\.[a-zA-Z]{2,4})(\?.*)?$/, '_' + size + 'x$1') : '');

  const fetchCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
  const getProduct = (handle) =>
    productCache[handle] || (productCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()));

  const SCROLL_SEL = ['.drawer__inner', 'cart-drawer-items', '.drawer__contents', '.drawer__cart-items-wrapper'];

  async function refreshDrawer() {
    const drawer = $('cart-drawer');
    if (!drawer) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const tops = SCROLL_SEL.map((s) => {
      const el = $(s, drawer);
      return el ? el.scrollTop : 0;
    });
    const [cart, sections] = await Promise.all([
      fetchCart(),
      fetch(root + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
    ]);
    lastCart = cart; // fresh before the re-render so extras rebuild correctly in the same tick
    drawer.renderContents({ id: Date.now(), sections: sections }, { skipOpen: true });
    requestAnimationFrame(() => {
      SCROLL_SEL.forEach((s, i) => {
        const el = $(s, drawer);
        if (el && tops[i]) el.scrollTop = tops[i];
      });
    });
  }

  // ---- Empty state: Continue shopping -> /collections -----------------------------------------------
  function fixContinueLinks() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const href = root + 'collections';
    document.querySelectorAll('cart-drawer .cart-drawer__empty-content a.button').forEach((a) => {
      if (a.getAttribute('href') !== href) a.setAttribute('href', href);
    });
  }

  // ---- Tier message (quantity read from the rendered DOM so it never lags the drawer) ---------------
  function domQty() {
    let q = 0;
    document.querySelectorAll('#CartDrawer-CartItems .quantity__input').forEach((i) => {
      q += parseInt(i.value, 10) || 0;
    });
    return q;
  }

  function ensureTierMsg() {
    const target = $('#ftp-bundle-tier-status');
    if (!target) return;
    const qty = domQty();
    let text = '';
    if (qty <= 1) text = 'Add 1 more item to unlock 10% off + free shipping';
    else if (qty === 2) text = 'Add 1 more item to unlock 20% off';
    else text = "You've unlocked 20% off";
    let msg = $('#ftp-tier-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.id = 'ftp-tier-msg';
      msg.className = 'ftp-tier-msg';
      target.before(msg);
    }
    if (msg.textContent !== text) msg.textContent = text;
    // The stepper is painted by bundle-upsell-modal.js on this event; fire it when the container is empty.
    if (!target.firstElementChild) document.dispatchEvent(new CustomEvent('ftp:cart-drawer-rendered'));
  }

  // ---- Full price / discount / subtotal breakdown (derived from the rendered drawer) ----------------
  function ensureSavings() {
    const totals = $('cart-drawer .totals');
    let box = $('#ftp-savings');
    if (!totals) {
      if (box) box.remove();
      return;
    }
    let full = 0;
    document.querySelectorAll('#CartDrawer-CartItems .cart-item').forEach((row) => {
      const old = row.querySelector('.cart-item__totals s');
      const cur = row.querySelector('.cart-item__totals span.price');
      const el = old || cur;
      if (el) full += parseMoney(el.textContent);
    });
    const sub = parseMoney(($('cart-drawer .totals__total-value') || {}).textContent);
    const save = full - sub;
    if (!full || save <= 0) {
      if (box) box.remove();
      return;
    }
    const titles = Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-drawer .discounts__discount'))
          .map((li) => li.textContent.replace(/\s+/g, ' ').replace(/\(-[^)]*\)/, '').trim())
          .filter(Boolean)
      )
    );
    const pct = Math.round((save / full) * 100);
    const label = (titles.length === 1 ? titles[0] : 'Discount') + ' (' + pct + '% off)';
    const key = full + '|' + save + '|' + label;
    if (!box) {
      box = document.createElement('div');
      box.id = 'ftp-savings';
      box.className = 'ftp-savings';
    }
    if (box.dataset.v !== key) {
      box.dataset.v = key;
      box.innerHTML =
        '<div class="ftp-savings__row"><span>Full price</span><span>' + money(full) + '</span></div>' +
        '<div class="ftp-savings__row ftp-savings__row--disc"><span>' + esc(label) + '</span><span>-' + money(save) + '</span></div>';
    }
    if (box.nextElementSibling !== totals) totals.before(box);
  }

  // ---- Trust badges --------------------------------------------------------------------------------
  function ensureTrust() {
    const ctas = $('cart-drawer .cart__ctas');
    const tpl = $('#ftp-trust-template');
    if (!ctas || !tpl || $('cart-drawer .ftp-trust')) return;
    ctas.after(tpl.content.cloneNode(true));
  }

  // ---- Variant dropdown per line -------------------------------------------------------------------
  async function swapVariant(item, newId, selectEl, previousId) {
    selectEl.disabled = true;
    try {
      const add = await fetch('/cart/add.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ items: [{ id: Number(newId), quantity: item.quantity }] }),
      });
      if (!add.ok) throw new Error('add failed');
      await fetch('/cart/change.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ id: item.key, quantity: 0 }) });
      await refreshDrawer();
    } catch (e) {
      console.error('FTP variant swap failed', e);
      selectEl.value = String(previousId);
      selectEl.disabled = false;
    }
  }

  async function ensureVariantSelects(cart) {
    const rows = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    for (const row of rows) {
      if (row.dataset.ftpVariant) continue;
      const idx = Number(String(row.id).replace('CartDrawer-Item-', '')) - 1;
      const vidAttr = row.querySelector('[data-quantity-variant-id]');
      const vid = vidAttr ? vidAttr.dataset.quantityVariantId : null;
      let item = cart.items[idx];
      if (!item || (vid && String(item.variant_id) !== String(vid))) item = cart.items.find((i) => String(i.variant_id) === String(vid));
      if (!item) continue; // cart data stale for this row; the fresh pass will handle it
      row.dataset.ftpVariant = '1';
      let product;
      try {
        product = await getProduct(item.handle);
      } catch (e) {
        continue;
      }
      if (!product || product.variants.length < 2) continue;
      const details = row.querySelector('.cart-item__details');
      const nameEl = row.querySelector('.cart-item__name');
      if (!details || !nameEl || row.querySelector('.ftp-line-variant')) continue;
      const label = product.options && product.options.length === 1 ? product.options[0].name || product.options[0] : 'Option';
      const wrap = document.createElement('div');
      wrap.className = 'ftp-line-variant';
      wrap.innerHTML =
        '<label class="ftp-line-variant__label">' + esc(label) + '</label><select aria-label="' + esc(label) + '">' +
        product.variants
          .map((v) => {
            const current = String(v.id) === String(item.variant_id);
            return '<option value="' + v.id + '"' + (current ? ' selected' : '') + (!v.available && !current ? ' disabled' : '') + '>' + esc(v.title) + (!v.available ? ' (sold out)' : '') + '</option>';
          })
          .join('') +
        '</select>';
      // Keep this dropdown's change event from reaching the theme's quantity-change handler.
      wrap.addEventListener('change', (e) => e.stopPropagation());
      nameEl.after(wrap);
      row.classList.add('ftp-has-variant-select');
      const sel = wrap.querySelector('select');
      sel.addEventListener('change', () => swapVariant(item, sel.value, sel, item.variant_id));
    }
  }

  // ---- Often bought together -----------------------------------------------------------------------
  async function loadRecs(anchor) {
    if (recs.anchor === anchor.product_id && recs.all) return recs.all;
    const res = await fetch('/recommendations/products.json?product_id=' + anchor.product_id + '&intent=complementary&limit=10', { headers: { Accept: 'application/json' } });
    const data = await res.json();
    recs = { anchor: anchor.product_id, all: data.products || [], idx: 0 };
    return recs.all;
  }

  async function ensureUpsell(cart) {
    const anchor = cart.items[0];
    if (!anchor) return;
    let all;
    try {
      all = await loadRecs(anchor);
    } catch (e) {
      console.error('FTP upsell failed', e);
      return;
    }
    const host = $('#CartDrawer-CartItems');
    if (!host) return;
    let box = $('#ftp-cart-upsell');
    if (!box) {
      box = document.createElement('div');
      box.id = 'ftp-cart-upsell';
      box.className = 'ftp-upsell';
      box.hidden = true;
      // Keep upsell dropdown changes from reaching the theme's quantity-change handler (it would show
      // "You can only add this item in increments of undefined"). The selects' own listeners still run.
      box.addEventListener('change', (e) => e.stopPropagation());
      host.appendChild(box);
    }
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const sig = Array.from(inCart).sort().join(',');
    if (box.dataset.sig === sig && box.firstElementChild) return;
    box.dataset.sig = sig;
    const products = all.filter((p) => p.available && !inCart.has(p.id));
    if (!products.length) {
      box.hidden = true;
      if (box.firstElementChild) box.innerHTML = '';
      return;
    }
    if (recs.idx >= products.length) recs.idx = 0;

    const draw = () => {
      const p = products[recs.idx];
      const first = p.variants.find((v) => v.available) || p.variants[0];
      const opts = (p.options || [])
        .map((o, i) => {
          const key = 'option' + (i + 1);
          return '<select data-opt="' + (i + 1) + '" aria-label="' + esc(o.name) + '">' + o.values.map((v) => '<option value="' + esc(v) + '"' + (v === first[key] ? ' selected' : '') + '>' + esc(v) + '</option>').join('') + '</select>';
        })
        .join('');
      const multi = products.length > 1;
      // Image is 25% larger than before (91px -> 114px); inline style so it wins over the stylesheet.
      box.innerHTML =
        '<p class="ftp-upsell__heading">Often bought together</p><div class="ftp-upsell__row">' +
        (multi ? '<button type="button" class="ftp-upsell__nav" data-dir="-1" aria-label="Previous">&#8592;</button>' : '') +
        '<div class="ftp-upsell__card"><img class="ftp-upsell__img" style="flex:0 0 114px;width:114px;max-height:114px;" src="' + scale(p.featured_image, 300) + '" alt="' + esc(p.title) + '" width="114" height="114" loading="lazy">' +
        '<div class="ftp-upsell__info"><p class="ftp-upsell__title">' + esc(p.title) + '</p><p class="ftp-upsell__price">' + money(first.price) + '</p>' +
        '<div class="ftp-upsell__opts">' + opts + '</div>' +
        '<button type="button" class="ftp-upsell__add">Add to cart</button></div></div>' +
        (multi ? '<button type="button" class="ftp-upsell__nav" data-dir="1" aria-label="Next">&#8594;</button>' : '') +
        '</div>';
      box.hidden = false;

      const selects = box.querySelectorAll('.ftp-upsell__opts select');
      const addBtn = box.querySelector('.ftp-upsell__add');
      const priceEl = box.querySelector('.ftp-upsell__price');
      const imgEl = box.querySelector('.ftp-upsell__img');
      let selected = first;
      const recompute = () => {
        const vals = Array.from(selects).map((s) => s.value);
        const match = p.variants.find((v) => vals.every((val, i) => v['option' + (i + 1)] === val));
        if (match) {
          selected = match;
          priceEl.textContent = money(match.price);
          const src = (match.featured_image && match.featured_image.src) || p.featured_image;
          if (src) imgEl.src = scale(src, 300);
          addBtn.disabled = !match.available;
          addBtn.textContent = match.available ? 'Add to cart' : 'Sold out';
        } else {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
        }
      };
      selects.forEach((s) => s.addEventListener('change', recompute));
      addBtn.addEventListener('click', async () => {
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';
        try {
          const r = await fetch('/cart/add.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ id: selected.id, quantity: 1 }) });
          if (!r.ok) throw new Error('add failed');
          await refreshDrawer();
        } catch (e) {
          console.error('FTP upsell add failed', e);
          addBtn.disabled = false;
          addBtn.textContent = 'Add to cart';
        }
      });
      box.querySelectorAll('.ftp-upsell__nav').forEach((b) =>
        b.addEventListener('click', () => {
          recs.idx = (recs.idx + Number(b.dataset.dir) + products.length) % products.length;
          draw();
        })
      );
    };
    draw();
  }

  // ---- Orchestration -------------------------------------------------------------------------------
  let running = false;
  let pending = false;
  let queued = false;

  async function enhance() {
    if (running) {
      pending = true;
      return;
    }
    running = true;
    try {
      fixContinueLinks();
      const drawer = $('cart-drawer');
      if (!drawer || drawer.classList.contains('is-empty') || !$('#CartDrawer-CartItems .cart-item')) return;
      // Pass 1: synchronous, from DOM + cached data, so nothing pops in late after a re-render.
      ensureTrust();
      ensureTierMsg();
      ensureSavings();
      if (lastCart) await Promise.all([ensureVariantSelects(lastCart), ensureUpsell(lastCart)]);
      // Pass 2: reconcile with the live cart (idempotent).
      const fresh = await fetchCart();
      lastCart = fresh;
      ensureTierMsg();
      ensureSavings();
      await Promise.all([ensureVariantSelects(fresh), ensureUpsell(fresh)]);
    } catch (e) {
      console.error('FTP cart extras failed', e);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      enhance();
    });
  }

  function start() {
    const drawer = $('cart-drawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
